/**
 * RoadmapMiroModal
 *
 * Export a Roadmap document to a Miro board, or import sticky notes from
 * an existing Miro board back into the Roadmap as conditions (nodes).
 *
 * Export shape: each RoadmapNode becomes a "priority" item with metadata
 * carrying swimlane and milestone information so the integrations engine
 * can group and position sticky notes on the board.
 *
 * Template ID: 'roadmap'
 * Auth: resolved from connector_configs (workspace or user scoped) via §2.1-compliant
 * DB read. Falls back to Settings → Connectors prompt if not configured.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  X,
  Loader2,
  ExternalLink,
  AlertTriangle,
  CheckCircle,
  Upload,
  Download,
  ArrowRight,
} from 'lucide-react';
import { getIntegrationsApi, type MiroBoard } from '../../lib/integrationsApi';
import { resolveConnectorConfig } from '../../lib/connectorConfigsService';
import type { ConnectorType } from '../../lib/connectors';
import { getCurrentWorkspaceId } from '../../lib/supabaseSync';
import { useUserStore } from '../../stores/prioritization/userStore';
import type {
  RoadmapDocument,
  RoadmapNode,
  RoadmapSwimlane,
  RoadmapMilestoneColumn,
} from '../../stores/prioritization/roadmapStore';

// ── Constants ─────────────────────────────────────────────────

const ROADMAP_TEMPLATE_ID = 'roadmap';

const TENSE_COLOR: Record<RoadmapNode['tense'], string> = {
  past: '#D5E8D4',    // soft green
  present: '#DAE8FC', // soft blue
  future: '#E1D5E7',  // soft violet
};

// ── Types ─────────────────────────────────────────────────────

type ModalTab = 'export' | 'import';

interface ExportSuccess {
  boardUrl?: string;
  itemsCreated: number;
  itemsUpdated: number;
  itemsSkipped: number;
}

interface ImportedNode {
  text: string;
  tense: RoadmapNode['tense'];
}

interface Props {
  doc: RoadmapDocument;
  nodes: RoadmapNode[];
  swimlanes: RoadmapSwimlane[];
  milestones: RoadmapMilestoneColumn[];
  onClose: () => void;
  onImportNodes: (nodes: ImportedNode[]) => Promise<void>;
}

// ── Helper: transform roadmap to export format ────────────────

function buildExportPayload(
  nodes: RoadmapNode[],
  swimlanes: RoadmapSwimlane[],
  milestones: RoadmapMilestoneColumn[],
) {
  const laneMap = new Map(swimlanes.map((l) => [l.id, l.name]));
  const milestoneMap = new Map(milestones.map((m) => [m.id, m.name]));

  return nodes.map((node) => ({
    id: node.id,
    title: node.text,
    description: node.status !== 'unknown' ? `Status: ${node.status}` : undefined,
    color: TENSE_COLOR[node.tense],
    metadata: {
      tense: node.tense,
      isGoal: node.isGoal,
      status: node.status,
      swimlane: node.laneId ? laneMap.get(node.laneId) ?? 'No lane' : 'No lane',
      milestone: node.milestoneId ? milestoneMap.get(node.milestoneId) ?? 'No milestone' : 'No milestone',
      swimlaneId: node.laneId,
      milestoneId: node.milestoneId,
    },
  }));
}

// ── Component ─────────────────────────────────────────────────

export function RoadmapMiroModal({
  doc,
  nodes,
  swimlanes,
  milestones,
  onClose,
  onImportNodes,
}: Props) {
  const userId = useUserStore((s) => s.userId);
  const [tab, setTab] = useState<ModalTab>('export');

  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [boards, setBoards] = useState<MiroBoard[]>([]);
  const [connected, setConnected] = useState(false);

  const [selectedBoard, setSelectedBoard] = useState('');
  const [newBoardName, setNewBoardName] = useState('');
  const [duplicateHandling, setDuplicateHandling] = useState<'skip' | 'update' | 'create'>('skip');
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [exportSuccess, setExportSuccess] = useState<ExportSuccess | null>(null);

  const [importBoard, setImportBoard] = useState('');
  const [isImporting, setIsImporting] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [importPreview, setImportPreview] = useState<ImportedNode[] | null>(null);
  const [isConfirmingImport, setIsConfirmingImport] = useState(false);

  const hasNodes = nodes.length > 0;
  const swimlaneNames = swimlanes.map((l) => l.name);

  const connect = useCallback(async () => {
    setIsConnecting(true);
    setConnectionError(null);
    try {
      const workspaceId = getCurrentWorkspaceId();
      if (!workspaceId) {
        setConnectionError('No active workspace. Please sign in and try again.');
        return;
      }
      const { data: config } = await resolveConnectorConfig(workspaceId, userId ?? null, 'miro' as ConnectorType);
      const token = (config?.authData as Record<string, string> | undefined)?.accessToken;
      if (!token) {
        setConnectionError('Miro is not connected. Go to Settings → Connectors to connect your Miro account first.');
        return;
      }
      const api = getIntegrationsApi(workspaceId);
      await api.connectMiro(token);
      const boardList = await api.listMiroBoards();
      setBoards(boardList);
      setConnected(true);
    } catch (err) {
      setConnectionError(err instanceof Error ? err.message : 'Failed to connect to Miro');
    } finally {
      setIsConnecting(false);
    }
  }, [userId]);

  useEffect(() => { connect(); }, [connect]);

  const handleExport = useCallback(async () => {
    setIsExporting(true);
    setExportError(null);
    try {
      const wsId = getCurrentWorkspaceId() ?? 'default';
      const api = getIntegrationsApi(wsId);
      const payload = buildExportPayload(nodes, swimlanes, milestones);
      const result = await api.exportToMiroTemplate(
        ROADMAP_TEMPLATE_ID,
        payload,
        {
          boardId: selectedBoard || undefined,
          createBoard: !selectedBoard,
          newBoardName: newBoardName.trim() || `${doc.title} — ${new Date().toLocaleDateString()}`,
          clearExisting: false,
          duplicateHandling,
          customCategories: swimlaneNames.length > 0 ? swimlaneNames : undefined,
        }
      );
      if (result.success) {
        setExportSuccess({
          boardUrl: result.boardUrl,
          itemsCreated: result.itemsCreated,
          itemsUpdated: result.itemsUpdated,
          itemsSkipped: result.itemsSkipped,
        });
        if (result.boardUrl) window.open(result.boardUrl, '_blank');
      } else {
        setExportError('Export failed. Please try again.');
      }
    } catch (err) {
      setExportError(err instanceof Error ? err.message : 'Export failed');
    } finally {
      setIsExporting(false);
    }
  }, [nodes, swimlanes, milestones, selectedBoard, newBoardName, duplicateHandling, doc.title, swimlaneNames]);

  const handlePreviewImport = useCallback(async () => {
    if (!importBoard) return;
    setIsImporting(true);
    setImportError(null);
    setImportPreview(null);
    try {
      const wsId = getCurrentWorkspaceId() ?? 'default';
      const api = getIntegrationsApi(wsId);
      const result = await api.importFromMiroTemplate(importBoard, ROADMAP_TEMPLATE_ID, {
        filterToTemplateBounds: false,
        includeUnzoned: true,
        minConfidence: 'nearest',
      });
      const mapped: ImportedNode[] = result.items.map((item) => ({
        text: item.content,
        tense: (item.toolAttributes?.tense as RoadmapNode['tense']) ?? 'future',
      }));
      setImportPreview(mapped);
    } catch (err) {
      setImportError(err instanceof Error ? err.message : 'Import preview failed');
    } finally {
      setIsImporting(false);
    }
  }, [importBoard]);

  const handleConfirmImport = useCallback(async () => {
    if (!importPreview) return;
    setIsConfirmingImport(true);
    await onImportNodes(importPreview);
    setIsConfirmingImport(false);
    onClose();
  }, [importPreview, onImportNodes, onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="w-full max-w-lg bg-[var(--bg-raised)] dark:bg-[var(--surface)] shadow-2xl flex flex-col overflow-hidden">

        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--rule)]">
          <div className="flex items-center gap-3">
            <div
              className="flex-shrink-0 w-7 h-7 flex items-center justify-center text-[var(--text-pri)] font-bold text-sm"
              style={{ backgroundColor: '#FFD02F' }}
            >
              M
            </div>
            <div>
              <h2 className="text-base font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
                Miro Integration
              </h2>
              <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] truncate max-w-[280px]">
                {doc.title}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)] text-[var(--text-ter)] hover:text-[var(--text-sec)] transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex border-b border-[var(--rule)] px-6">
          {(['export', 'import'] as ModalTab[]).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`flex items-center gap-1.5 pb-3 pt-3 mr-6 text-sm font-medium border-b-2 transition-colors capitalize ${
                tab === t
                  ? 'border-[var(--slate)] text-[var(--text-ter)] dark:text-[var(--text-ter)]'
                  : 'border-transparent text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-ter)]'
              }`}
            >
              {t === 'export' ? <Upload className="h-3.5 w-3.5" /> : <Download className="h-3.5 w-3.5" />}
              {t === 'export' ? 'Send to Miro' : 'Import from Miro'}
            </button>
          ))}
        </div>

        <div className="p-6 flex-1 overflow-y-auto space-y-4">

          {isConnecting && (
            <div className="flex items-center gap-2 text-sm text-[var(--text-ter)] dark:text-[var(--text-ter)]">
              <Loader2 className="h-4 w-4 animate-spin" />
              Connecting to Miro…
            </div>
          )}
          {connectionError && (
            <div className="flex items-start gap-2 p-3 bg-[var(--coral-dim)] dark:bg-[var(--coral)]/20 border border-[var(--coral)] dark:border-[var(--coral)] text-sm text-[var(--coral)] dark:text-[var(--coral)]">
              <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <span>{connectionError}</span>
            </div>
          )}

          {tab === 'export' && connected && !exportSuccess && (
            <div className="space-y-4">
              <div className="p-3 bg-[var(--surface)] dark:bg-[var(--bg-raised)]/50 text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)]">
                <strong>{nodes.length}</strong> condition{nodes.length !== 1 ? 's' : ''} will be exported as sticky notes.
                {swimlanes.length > 0 && (
                  <span className="ml-1">
                    Grouped by <strong>{swimlanes.length}</strong> swimlane{swimlanes.length !== 1 ? 's' : ''}.
                  </span>
                )}
                {!hasNodes && (
                  <p className="text-[var(--mustard)] dark:text-[var(--mustard)] mt-1">
                    No conditions to export yet. Add nodes to the roadmap first.
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-1">
                  Destination board
                </label>
                <select
                  value={selectedBoard}
                  onChange={(e) => setSelectedBoard(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] text-[var(--text-pri)] dark:text-[var(--text-pri)] focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">Create new board</option>
                  {boards.map((b) => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </select>
              </div>

              {!selectedBoard && (
                <div>
                  <label className="block text-sm font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-1">
                    New board name
                  </label>
                  <input
                    type="text"
                    value={newBoardName}
                    onChange={(e) => setNewBoardName(e.target.value)}
                    placeholder={`${doc.title} — ${new Date().toLocaleDateString()}`}
                    className="w-full px-3 py-2 text-sm border border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] text-[var(--text-pri)] dark:text-[var(--text-pri)] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              )}

              {selectedBoard && (
                <div>
                  <label className="block text-sm font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-1">
                    If duplicates found
                  </label>
                  <select
                    value={duplicateHandling}
                    onChange={(e) => setDuplicateHandling(e.target.value as 'skip' | 'update' | 'create')}
                    className="w-full px-3 py-2 text-sm border border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] text-[var(--text-pri)] dark:text-[var(--text-pri)] focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="skip">Skip — don't create if title already exists</option>
                    <option value="update">Update — move to correct position</option>
                    <option value="create">Create anyway — may produce duplicates</option>
                  </select>
                </div>
              )}

              <div className="flex flex-wrap gap-2 pt-1">
                {([['past', 'Past (green)'], ['present', 'Present (blue)'], ['future', 'Future (violet)']] as const).map(([tense, label]) => (
                  <span key={tense} className="flex items-center gap-1 text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
                    <span className="w-3 h-3 inline-block border border-[var(--rule)]" style={{ backgroundColor: TENSE_COLOR[tense] }} />
                    {label}
                  </span>
                ))}
              </div>

              {exportError && (
                <div className="flex items-start gap-2 p-3 bg-[var(--coral-dim)] dark:bg-[var(--coral)]/20 border border-[var(--coral)] dark:border-[var(--coral)] text-sm text-[var(--coral)] dark:text-[var(--coral)]">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  {exportError}
                </div>
              )}
            </div>
          )}

          {tab === 'export' && exportSuccess && (
            <div className="p-4 bg-[var(--green-dim)] dark:bg-[var(--green)]/20 border border-[var(--green)] dark:border-[var(--green)]">
              <div className="flex items-start gap-2">
                <CheckCircle className="h-5 w-5 text-[var(--green)] flex-shrink-0 mt-0.5" />
                <div className="space-y-1">
                  <p className="text-sm font-medium text-[var(--green)] dark:text-[var(--text-ter)]">Export complete!</p>
                  {exportSuccess.itemsCreated > 0 && (
                    <p className="text-sm text-[var(--green)] dark:text-[var(--text-ter)]">Created {exportSuccess.itemsCreated} sticky note{exportSuccess.itemsCreated !== 1 ? 's' : ''}.</p>
                  )}
                  {exportSuccess.itemsUpdated > 0 && (
                    <p className="text-sm text-[var(--green)] dark:text-[var(--text-ter)]">Updated {exportSuccess.itemsUpdated} existing note{exportSuccess.itemsUpdated !== 1 ? 's' : ''}.</p>
                  )}
                  {exportSuccess.itemsSkipped > 0 && (
                    <p className="text-sm text-[var(--green)] dark:text-[var(--text-ter)]">Skipped {exportSuccess.itemsSkipped} duplicate{exportSuccess.itemsSkipped !== 1 ? 's' : ''}.</p>
                  )}
                  {exportSuccess.boardUrl && (
                    <a
                      href={exportSuccess.boardUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 mt-1 text-sm text-[var(--green)] dark:text-[var(--text-ter)] hover:underline font-medium"
                    >
                      Open board in Miro <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </div>
              </div>
            </div>
          )}

          {tab === 'import' && connected && !importPreview && (
            <div className="space-y-4">
              <p className="text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)] leading-relaxed">
                Select a Miro board that was previously created from a roadmap template.
                Sticky notes will be imported as new conditions.
              </p>
              <div>
                <label className="block text-sm font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-1">
                  Source board
                </label>
                <select
                  value={importBoard}
                  onChange={(e) => setImportBoard(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] text-[var(--text-pri)] dark:text-[var(--text-pri)] focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">Select a board…</option>
                  {boards.map((b) => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </select>
              </div>
              {importError && (
                <div className="flex items-start gap-2 p-3 bg-[var(--coral-dim)] dark:bg-[var(--coral)]/20 border border-[var(--coral)] dark:border-[var(--coral)] text-sm text-[var(--coral)] dark:text-[var(--coral)]">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  {importError}
                </div>
              )}
            </div>
          )}

          {tab === 'import' && importPreview && (
            <div className="space-y-4">
              <div className="p-3 bg-[var(--slate-dim)] dark:bg-[var(--slate)]/20 border border-[var(--slate)] dark:border-[var(--slate)] text-sm text-[var(--text-sec)]">
                Found <strong>{importPreview.length}</strong> condition{importPreview.length !== 1 ? 's' : ''} to import.
              </div>
              <div className="max-h-48 overflow-y-auto border border-[var(--rule)] divide-y divide-[var(--rule)] dark:divide-[var(--rule)]">
                {importPreview.map((n, i) => (
                  <div key={i} className="flex items-center gap-2 px-3 py-2">
                    <span
                      className="flex-shrink-0 w-2.5 h-2.5 border border-[var(--rule)]"
                      style={{ backgroundColor: TENSE_COLOR[n.tense] }}
                    />
                    <span className="text-sm text-[var(--text-pri)] dark:text-[var(--text-pri)] line-clamp-1">{n.text}</span>
                    <span className="ml-auto text-xs text-[var(--text-ter)] capitalize">{n.tense}</span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
                These will be added as new nodes with no lane or milestone assignment. You can organize them in the grid after import.
              </p>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-[var(--rule)] flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)] transition-colors"
          >
            {exportSuccess ? 'Close' : 'Cancel'}
          </button>

          {tab === 'export' && connected && !exportSuccess && (
            <button
              onClick={handleExport}
              disabled={isExporting || !hasNodes}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-[var(--text-pri)] disabled:opacity-50 transition-opacity"
              style={{ backgroundColor: isExporting ? '#FFD02F99' : '#FFD02F' }}
            >
              {isExporting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Exporting…
                </>
              ) : (
                <>
                  <ArrowRight className="h-4 w-4" />
                  Send to Miro
                </>
              )}
            </button>
          )}

          {tab === 'import' && connected && !importPreview && (
            <button
              onClick={handlePreviewImport}
              disabled={!importBoard || isImporting}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-[var(--slate)] hover:bg-[var(--slate)] disabled:opacity-50 text-white transition-colors"
            >
              {isImporting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading…
                </>
              ) : (
                <>
                  <Download className="h-4 w-4" />
                  Preview import
                </>
              )}
            </button>
          )}

          {tab === 'import' && importPreview && (
            <>
              <button
                onClick={() => setImportPreview(null)}
                className="px-4 py-2 text-sm text-[var(--text-ter)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)] transition-colors"
              >
                Back
              </button>
              <button
                onClick={handleConfirmImport}
                disabled={isConfirmingImport}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-[var(--slate)] hover:bg-[var(--slate)] disabled:opacity-50 text-white transition-colors"
              >
                {isConfirmingImport ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Importing…
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4" />
                    Import {importPreview.length} node{importPreview.length !== 1 ? 's' : ''}
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
